/**
 * Compile-time list & union type of available icon names.
 *
 * The list below mirrors the `.png` files in `src/statics/images/icons/`.
 * Because Vite's `import.meta.glob` types its keys as plain `string`,
 * a literal-union type cannot be inferred from it directly. We therefore
 * declare the list as a `const` tuple here, and use a dev-time assertion
 * (below) to catch any drift between this tuple and the actual filesystem.
 *
 * To add or remove an icon:
 *   1. Add/remove its `.png` in `src/statics/images/icons/`.
 *   2. Add/remove its name in `ICON_NAMES` below.
 * Forgetting step 2 will throw in development, immediately surfacing the
 * mismatch.
 */
export const ICON_NAMES = [
  'bracket-group',
  'bracket-knockout',
  'cup-icon-euro',
  'cup-icon-wc',
  'football',
  'highlight',
  'red-card',
  'second-yellow-card',
  'stadium',
  'stats',
  'substitution',
  'summary',
  'var',
  'whistle',
  'yellow-card',
] as const;

/** Union of valid icon names (e.g. 'football' | 'red-card' | …). */
export type IconName = (typeof ICON_NAMES)[number];

/** Type guard for narrowing arbitrary strings to a valid `IconName`. */
export const isIconName = (value: string): value is IconName =>
  (ICON_NAMES as readonly string[]).includes(value);

// ---------------------------------------------------------------------------
// Dev-time drift check: throws a loud error if the hardcoded list above
// gets out of sync with the actual files on disk. Stripped from production
// builds by Vite's dead-code elimination.
// ---------------------------------------------------------------------------
if (import.meta.env?.DEV) {
  const onDisk = Object.keys(
    import.meta.glob('/src/statics/images/icons/*.png', { eager: true })
  )
    .map((p) =>
      p
        .split('/')
        .pop()!
        .replace(/\.png$/, '')
    )
    .sort();

  const declared = [...ICON_NAMES].sort();
  const missing = onDisk.filter((n) => !declared.includes(n as IconName));
  const extra = declared.filter((n) => !onDisk.includes(n));

  if (missing.length || extra.length) {
    // eslint-disable-next-line no-console
    console.error(
      '[Icons] ICON_NAMES is out of sync with src/statics/images/icons/.\n' +
        (missing.length ?
          `  Missing from ICON_NAMES: ${missing.join(', ')}\n`
        : '') +
        (extra.length ? `  Listed but not on disk:  ${extra.join(', ')}\n` : '')
    );
  }
}
